import { getConnectionOffset, calculatePositions } from './math.js';
import { reduceSVGToGroup, wrapGroupsInSVG, removeConnectionPoints } from './text.js';
import {
  stringToDoc,
  docToString,
  normalize as normalizeCore,
  getDocumentMetadata,
  applySubstitutionClasses,
} from './dom.js';

let TIMINGS = false;

// combine circuits at the specified positions, using the height and width for the combined circuit calculated from metadata for each circuit
function _composeCircuitsUsingMetadata(circuits, positions) {
  let groups = [];
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (var i = 0; i <= circuits.length - 1; i++) {
    maxX = Math.max(maxX, positions[i].x + circuits[i].metadata.docWidth);
    maxY = Math.max(maxY, positions[i].y + circuits[i].metadata.docHeight);
    groups.push(
      reduceSVGToGroup(circuits[i].circuit, positions[i].x, positions[i].y, `group-${i}`, circuits[i].metadata.matrix)
    );
  }
  return wrapGroupsInSVG(groups, maxX, maxY);
}

// combine circuits at the specified positions, using the supplied height and width for the combined circuit
function _composeCircuitsToFixedDimensions(circuits, positions, width, height) {
  var groups = [];
  for (var i = 0; i <= circuits.length - 1; i++) {
    groups.push(reduceSVGToGroup(circuits[i], positions[i].x, positions[i].y, `group-${i}`));
  }
  let circuit = wrapGroupsInSVG(groups, width, height);
  return circuit;
}

// type SVGConnectionPoint = { name: string, type: string, x: number, y: number, index: number, offset: number};
export default {
  // import the content of a URL file.
  loadFile(fileName, abortController) {
    if (TIMINGS) console.time('load');
    return fetch(fileName, { signal: abortController?.signal })
      .then((response) => {
        if (TIMINGS) console.timeEnd('load');
        if (response.status > 400) {
          return '';
        } else {
          return response.text();
        }
      })
      .catch((error) => {
        if (TIMINGS) console.timeEnd('load');
        if (!error.type || error.type !== 'aborted') {
          console.log(error);
        }
      });
  },

  // clean up a circuit, deleting namespaces and associated attributes.
  normalize(circuit) {
    if (TIMINGS) console.time('normalize');

    var doc = stringToDoc(circuit);
    normalizeCore(doc);
    var result = docToString(doc);

    if (TIMINGS) console.timeEnd('normalize');
    return result;
  },

  prepare(circuit) {
    if (TIMINGS) console.time('prepare');

    var doc = stringToDoc(circuit);
    var metadata = getDocumentMetadata(doc);
    normalizeCore(doc);
    applySubstitutionClasses(doc);
    var result = docToString(doc);

    if (TIMINGS) console.timeEnd('prepare');
    return { circuit: result, metadata: metadata };
  },

  // process from content and substitute key values.
  substituteCircuitValues(circuit, changeList, useBraces) {
    if (TIMINGS) console.time('substitute');
    var svg = typeof circuit === 'string' ? circuit : circuit.circuit;
    if (changeList) {
      for (var changeKey in changeList) {
        var replValue = changeList[changeKey];
        svg = svg.replace(new RegExp(useBraces ? `{${changeKey}}` : `${changeKey}`, 'gi'), `${replValue}`);
      }
    }
    let result = typeof circuit === 'string' ? svg : { circuit: svg, metadata: circuit.metadata };
    if (TIMINGS) console.timeEnd('substitute');
    return result;
  },

  // comnbine a circuit and position, returns a modified circuit
  // with the position values set as svg attributes.
  composeCircuits(circuits, positions, width, height) {
    if (TIMINGS) console.time('compose');
    let circuit = undefined;
    if (circuits.length > 0 && typeof circuits[0] === 'string') {
      circuit = _composeCircuitsToFixedDimensions(circuits, positions, width, height);
    } else if (circuits.length > 0 && 'metadata' in circuits[0]) {
      circuit = _composeCircuitsUsingMetadata(circuits, positions);
    }
    if (TIMINGS) console.timeEnd('compose');
    return circuit;
  },

  // return circuit composed of multiple connected circuits
  stitchCircuits(circuits, connections) {
    let circuitsWithConnections = this.stitchCircuitsWithConnectionPoints(circuits, connections);
    return circuitsWithConnections.circuit;
  },

  // return circuit composed of multiple connected circuits, delivering connection point information as well
  stitchCircuitsWithConnectionPoints(circuits, connections) {
    if (TIMINGS) console.time('stitch');

    if (circuits.length < 2 || connections.length < 1) {
      if (TIMINGS) console.timeEnd('stitch');
      throw 'Need at least two circuits and one connection';
    }
    let offsets = connections.map((connection) => {
      return getConnectionOffset(connection, circuits);
    });
    let positions = calculatePositions(connections, offsets, circuits.length);
    let min = {
      x: Math.min.apply(
        null,
        positions.map((item) => item.x)
      ),
      y: Math.min.apply(
        null,
        positions.map((item) => item.y)
      ),
    };
    let origins = positions.map((item) => {
      return { x: item.x - min.x, y: item.y - min.y };
    });
    let deliveredPoints = circuits.map((circuit, circuitIndex) => {
      return circuit.metadata.connectionPoints.map((cp) => {
        return {
          type: cp.type,
          name: cp.name,
          position: [origins[circuitIndex].x + cp.position[0], origins[circuitIndex].y + cp.position[1]],
        };
      });
    });
    let cleanedCircuits = circuits.map((item, index) => {
      return { circuit: removeConnectionPoints(item.circuit, connections, index), metadata: item.metadata };
    });
    let result = _composeCircuitsUsingMetadata(cleanedCircuits, origins);
    if (TIMINGS) console.timeEnd('stitch');
    return { circuit: result, circuitConnectionPoints: deliveredPoints };
  },

  // insert an svg string into a DOM element.
  insert(elementId, circuit) {
    if (TIMINGS) console.time('insert');
    let svg = typeof circuit === 'string' ? circuit : circuit.circuit;
    var element = document.getElementById(elementId);
    if (element) {
      element.innerHTML = svg;
    }
    if (TIMINGS) console.timeEnd('insert');
    return document;
  },

  // remove a DOM element.
  remove(elementId) {
    if (TIMINGS) console.time('remove');
    var element = document.getElementById(elementId);
    if (element) {
      element.innerHTML = '';
    }
    if (TIMINGS) console.timeEnd('remove');
    return document;
  },
};
